// The compatibility checker's words and formatters. Pure: shared by the server
// page (the crawlable default result) and the client widget, so both say the
// same thing about the same report.

import type { Product } from '@roomtreatment/av-catalog';
import type { SystemLayout, SystemReport, SystemRole } from '@roomtreatment/av-compat';
import { HEADROOM_BAND_LABEL } from '@/components/room/gear/labels';
import type { ReadoutTone } from '@/components/seo/roles';

export const ROLE_LABEL: Record<SystemRole, string> = {
  avr: 'Receiver or processor',
  amps: 'Power amp (optional)',
  fronts: 'Front speakers',
  center: 'Center speaker',
  surrounds: 'Surround speakers',
  heights: 'Height speakers',
  sub: 'Subwoofer',
};

/** Lowercase role name for mid-sentence use. */
const ROLE_NOUN: Record<SystemRole, string> = {
  avr: 'a receiver',
  amps: 'a power amp',
  fronts: 'front speakers',
  center: 'a center speaker',
  surrounds: 'surround speakers',
  heights: 'height speakers',
  sub: 'a subwoofer',
};

export const LAYOUT_LABEL: Record<SystemLayout, string> = {
  stereo: 'Stereo',
  '5.1': '5.1',
  '5.1.2': '5.1.2',
  '5.1.4': '5.1.4',
  '7.1': '7.1',
  '7.1.2': '7.1.2',
  '7.1.4': '7.1.4',
};

export function productLabel(p: Pick<Product, 'brand' | 'model'>): string {
  return `${p.brand} ${p.model}`;
}

/** The product page for a catalogue id `brand.model` (plan S24 slug rule). */
export function gearHref(productId: string): string {
  return `/gear/${productId.replace('.', '-')}`;
}

export type VerdictTone = 'ok' | 'warn' | 'danger' | 'neutral';

export function verdictCopy(report: SystemReport): { title: string; detail: string; tone: VerdictTone } {
  if (report.verdict === 'issues') {
    const n = report.issues.length;
    const errors = report.issues.some((i) => i.severity === 'error');
    return {
      title: `${n} ${n === 1 ? 'problem' : 'problems'} found`,
      detail: errors ? 'This system won’t work as picked. Each problem below says what to change.' : 'This system works, but it could be better.',
      tone: errors ? 'danger' : 'warn',
    };
  }
  if (report.verdict === 'incomplete') {
    const detail = report.missing.length > 0
      ? `Choose ${joinList(report.missing.map((r) => ROLE_NOUN[r]))} to finish the check.`
      : 'A product is missing a published spec, so not every check could run.';
    return { title: 'Not fully checked', detail, tone: 'neutral' };
  }
  return { title: 'Compatible', detail: 'Every spec check passed for these products.', tone: 'ok' };
}

/** A readout's status hue (roles.ts `ReadoutTone`) is set only when the value
 *  reports a real outcome worth calling out (a real problem, or a "you'll
 *  want to change this"), never on a plain pass: a passing readout wears no
 *  hue at all, per the Status-readout rule ("never as emphasis"). */
export type { ReadoutTone };

export interface Readout {
  label: string;
  value: string;
  note: string;
  tone?: ReadoutTone;
}

/** Channels, power, load and sub outputs: the receiver and amps against the speakers. */
export function headroomReadouts(report: SystemReport): Readout[] | null {
  const h = report.headroom;
  if (!h) return null;
  const { chain, needs } = h;

  const POWER_TONE: Record<string, ReadoutTone | undefined> = {
    'too-weak': 'danger',
    'just-enough': 'warn',
  };

  const power: Readout = chain.power && needs.power
    ? {
      label: 'Power',
      value: `${Math.round(chain.power.ratedW)} W`,
      note: `${HEADROOM_BAND_LABEL[chain.power.band]}. About ${needs.power.wattsPerChannel} W gives ${needs.peakDb} dB peaks at ${h.seatDistanceM} m`,
      tone: POWER_TONE[chain.power.band],
    }
    : { label: 'Power', value: '—', note: needs.unknownSensitivity.length > 0 ? 'Sensitivity not published' : 'Choose speakers to size it' };

  const LOAD: Record<typeof chain.load, { value: string; note: string; tone?: ReadoutTone }> = {
    ok: { value: 'OK', note: 'Rated for these speakers' },
    'set-4-ohm': { value: 'Set 4 Ω', note: 'Change it in speaker setup', tone: 'warn' },
    'too-low': {
      value: 'Too low',
      note: needs.minLoadOhm != null ? `Speakers dip to ${needs.minLoadOhm} Ω` : 'Below its rating',
      tone: 'danger',
    },
    unknown: { value: '—', note: 'Minimum impedance not published' },
  };

  const channelsOk = chain.channels.have >= chain.channels.need;
  const subOk = chain.subOutputs ? chain.subOutputs.have >= chain.subOutputs.need : true;

  return [
    {
      label: 'Amp channels',
      value: `${chain.channels.have} of ${chain.channels.need}`,
      note: channelsOk ? 'Enough for every speaker' : 'Too few for every speaker',
      tone: channelsOk ? undefined : 'danger',
    },
    power,
    { label: 'Speaker load', ...LOAD[chain.load] },
    chain.subOutputs
      ? {
        label: 'Sub outputs',
        value: String(chain.subOutputs.have),
        note: subOk ? 'Enough for a subwoofer' : 'No subwoofer output',
        tone: subOk ? undefined : 'danger',
      }
      : { label: 'Sub outputs', value: '—', note: 'No subwoofer picked' },
  ];
}

function joinList(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}
